import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { InfraAPI } from "@/lib/infra-api";
import { SlideOver, SaveStatus } from "./SlideOver";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  FileText, Upload, Download, Trash2, ShieldCheck, AlertTriangle, Clock,
  Search, ExternalLink, Loader2, CheckCircle2, XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Catalog of document types the campus is expected to maintain.
const DOC_TYPES = [
  { kind: "building_plan", label: "Building Plan", icon: FileText, group: "Construction" },
  { kind: "occupancy_cert", label: "Occupancy Certificate", icon: FileText, group: "Construction" },
  { kind: "electrical_cert", label: "Electrical Certificate", icon: FileText, group: "Construction" },
  { kind: "lift_cert", label: "Lift Certificate", icon: FileText, group: "Construction" },
  { kind: "fire_noc", label: "Fire NOC", icon: FileText, group: "Safety" },
  { kind: "safety_audit", label: "Safety Audit Report", icon: FileText, group: "Safety" },
  { kind: "insurance", label: "Insurance Policy", icon: FileText, group: "Insurance" },
  { kind: "naac", label: "NAAC Report", icon: FileText, group: "Accreditation" },
  { kind: "nba", label: "NBA Report", icon: FileText, group: "Accreditation" },
  { kind: "aicte", label: "AICTE Approval", icon: FileText, group: "Accreditation" },
  { kind: "ugc", label: "UGC Approval", icon: FileText, group: "Accreditation" },
  { kind: "govt_approvals", label: "Government Approvals", icon: FileText, group: "Accreditation" },
  { kind: "infra_audit", label: "Infrastructure Audit", icon: FileText, group: "Audit" },
  { kind: "blueprints", label: "Blueprints", icon: FileText, group: "Construction" },
  { kind: "floor_plans", label: "Floor Plans", icon: FileText, group: "Construction" },
];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(String(dateStr).slice(0, 10));
    const now = new Date(new Date().toISOString().slice(0, 10));
    return Math.round((d - now) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

function statusFromDays(days) {
  if (days == null) return { label: "No expiry set", tone: "slate" };
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, tone: "rose" };
  if (days <= 30) return { label: `Expiring in ${days}d`, tone: "rose" };
  if (days <= 60) return { label: `Expiring in ${days}d`, tone: "amber" };
  if (days <= 90) return { label: `Expiring in ${days}d`, tone: "amber" };
  return { label: `Valid — ${days}d left`, tone: "emerald" };
}

const TONE = {
  slate: "bg-slate-100 text-slate-600",
  rose: "bg-rose-50 text-rose-700",
  amber: "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
};

export function DocumentsPanel({ open, onClose, campusId }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("idle");
  const [q, setQ] = useState("");

  const listQ = useQuery({
    queryKey: ["infra-documents", campusId],
    queryFn: () => api.get(`/infrastructure/documents`, { params: { campus_id: campusId } }).then((r) => r.data),
    enabled: !!campusId && open,
  });

  const byKind = useMemo(() => {
    const m = new Map();
    for (const d of listQ.data || []) {
      const arr = m.get(d.kind) || [];
      arr.push(d);
      m.set(d.kind, arr);
    }
    return m;
  }, [listQ.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["infra-documents", campusId] });
    qc.invalidateQueries({ queryKey: ["infra-dashboard", campusId] });
  };

  const uploadFor = async (spec, file) => {
    setStatus("saving");
    try {
      const { file_id } = await InfraAPI.upload(file, campusId);
      await api.post(`/infrastructure/documents`, {
        campus_id: campusId,
        kind: spec.kind,
        name: spec.label,
        config: { file_id, filename: file.name, uploaded_at: new Date().toISOString() },
      });
      setStatus("saved");
      toast.success(`${spec.label} uploaded`);
      invalidate();
      setTimeout(() => setStatus("idle"), 1200);
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const patchMeta = async (doc, patch) => {
    setStatus("saving");
    try {
      const nextConfig = { ...(doc.config || {}), ...patch };
      await api.patch(`/infrastructure/documents/${doc.id}`, { config: nextConfig });
      setStatus("saved");
      invalidate();
      setTimeout(() => setStatus("idle"), 1200);
    } catch {
      setStatus("error");
      toast.error("Save failed");
    }
  };

  const removeDoc = async (doc) => {
    setStatus("saving");
    try {
      await api.delete(`/infrastructure/documents/${doc.id}`);
      setStatus("saved");
      invalidate();
      setTimeout(() => setStatus("idle"), 1200);
    } catch {
      setStatus("error");
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const spec of DOC_TYPES) {
      if (q && !spec.label.toLowerCase().includes(q.toLowerCase())) continue;
      (g[spec.group] = g[spec.group] || []).push(spec);
    }
    return g;
  }, [q]);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Documents & Compliance"
      subtitle="Building plans, NOCs, insurance, audit reports and certificates."
      right={<SaveStatus status={status} />}
      width="max-w-4xl"
      testid="documents"
    >
      <div className="p-6 space-y-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search documents…"
            className="pl-9"
            data-testid="docs-search"
          />
        </div>

        {listQ.isLoading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          Object.entries(grouped).map(([groupLabel, items]) => (
            <div key={groupLabel}>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">{groupLabel}</div>
              <ul className="space-y-2">
                {items.map((spec) => (
                  <DocRow
                    key={spec.kind}
                    spec={spec}
                    docs={byKind.get(spec.kind) || []}
                    onUpload={(file) => uploadFor(spec, file)}
                    onPatchMeta={patchMeta}
                    onRemove={removeDoc}
                  />
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </SlideOver>
  );
}

function DocRow({ spec, docs, onUpload, onPatchMeta, onRemove }) {
  const Icon = spec.icon;
  const primary = docs[0];  // most-recent
  const cfg = primary?.config || {};
  const days = daysUntil(cfg.expiry);
  const state = statusFromDays(days);

  return (
    <li className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-3">
        <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center shrink-0",
          primary ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-400")}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{spec.label}</div>
          {primary ? (
            <div className="text-xs text-slate-500 truncate">
              {cfg.filename || "file"}
              {cfg.certificate_number && ` · ${cfg.certificate_number}`}
              {cfg.issuer && ` · ${cfg.issuer}`}
            </div>
          ) : (
            <div className="text-xs text-slate-400">No file uploaded yet</div>
          )}
        </div>

        {primary && (
          <span className={cn("text-[10px] font-semibold px-2 py-1 rounded-full uppercase tracking-wide", TONE[state.tone])} data-testid={`doc-status-${spec.kind}`}>
            {state.label}
          </span>
        )}

        {!primary ? (
          <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 cursor-pointer">
            <Upload className="h-3.5 w-3.5" />
            Upload
            <input
              type="file"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
              data-testid={`doc-upload-${spec.kind}`}
            />
          </label>
        ) : (
          <div className="flex items-center gap-1">
            <a
              href={InfraAPI.fileUrl(cfg.file_id)}
              target="_blank" rel="noreferrer"
              className="h-8 w-8 rounded-md hover:bg-slate-100 text-slate-500 flex items-center justify-center"
              title="Open"
              data-testid={`doc-open-${spec.kind}`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <label
              className="h-8 w-8 rounded-md hover:bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
              title="Replace"
            >
              <Upload className="h-3.5 w-3.5" />
              <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
            </label>
            <button
              type="button"
              onClick={() => onRemove(primary)}
              className="h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center"
              title="Remove"
              data-testid={`doc-del-${spec.kind}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {primary && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Meta label="Issued On">
            <Input
              type="date"
              defaultValue={cfg.issue_date || ""}
              onBlur={(e) => onPatchMeta(primary, { issue_date: e.target.value })}
              data-testid={`doc-issue-${spec.kind}`}
            />
          </Meta>
          <Meta label="Expires On">
            <Input
              type="date"
              defaultValue={cfg.expiry || ""}
              onBlur={(e) => onPatchMeta(primary, { expiry: e.target.value })}
              data-testid={`doc-expiry-${spec.kind}`}
            />
          </Meta>
          <Meta label="Certificate #">
            <Input
              defaultValue={cfg.certificate_number || ""}
              onBlur={(e) => onPatchMeta(primary, { certificate_number: e.target.value })}
              data-testid={`doc-cert-${spec.kind}`}
            />
          </Meta>
          <Meta label="Issued By">
            <Input
              defaultValue={cfg.issuer || ""}
              onBlur={(e) => onPatchMeta(primary, { issuer: e.target.value })}
              placeholder="Fire Dept / Board / …"
              data-testid={`doc-issuer-${spec.kind}`}
            />
          </Meta>
        </div>
      )}
    </li>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
